import { ref, reactive, computed } from 'vue';

/**
 * INTERNAL admin: Atlas MCP connector estate across every tenant.
 *
 * Lists customers (+ the RoleMapper Internal tenant, for staff connectors)
 * with the OAuth clients and connector tokens registered against them, and
 * drives the "mint + register" shortcut that yields a client_id / secret for
 * hosts that can't do dynamic registration (Copilot Studio Manual mode).
 *
 * Framework-agnostic `self` composable shared by rm-portal (Vue 2.7) and
 * rm-portal-beta (Vue 3); the caller injects an axios-like http client.
 * Backed by rm-api `api/oauth/admin/internal/*` (@isInternal).
 *
 * @param {import('axios').AxiosInstance} http
 * @param {{ confirm?: (message: string) => boolean }} [opts]
 */

export const DEFAULT_SCOPE = 'openid profile.read skill.read hierarchy.read offline_access';

// Known MCP host callbacks.
//
// The Microsoft preset covers ALL THREE Microsoft surfaces with one client, so
// a customer gets a single client_id + secret whichever way they connect:
//   • Copilot Studio wraps the MCP server in a Power Platform connector whose
//     callback carries a PER-CONNECTOR suffix
//     (…/redirect/<connector-id>) that only exists after the connector is
//     created on the Microsoft side. atlas-mcp accepts a trailing "/*" path
//     wildcard on that host (provider.ts, OAUTH_WILDCARD_REDIRECT_HOSTS), so
//     it works first time — no round trip to discover the suffix.
//   • Microsoft 365 Copilot declarative agents sign in via the Teams callback.
//   • The Microsoft 365 Agents Toolkit (VS Code) uses the vscode.dev callback
//     when it connects to the server to fetch tools while building an agent.
// Registering only the APIM pair is the classic "invalid_redirect_uri" on a
// declarative agent's first sign-in (live miss, 2026-09-11).
export const HOST_PRESETS = [
	{
		key: 'copilot',
		text: 'Microsoft Copilot (Studio, M365 declarative agent, Agents Toolkit)',
		client_name: 'Microsoft Copilot',
		redirect_uris: [
			'https://global.consent.azure-apim.net/redirect/*',
			'https://global.consent.azure-apim.net/redirect',
			'https://teams.microsoft.com/api/platform/v1.0/oAuthRedirect',
			'https://vscode.dev/redirect',
		],
		confidential: true,
	},
	{
		key: 'claude',
		text: 'Claude (claude.ai / Desktop)',
		client_name: 'Claude',
		redirect_uris: ['https://claude.ai/api/mcp/auth_callback'],
		confidential: false,
	},
	{
		key: 'custom',
		text: 'Custom',
		client_name: '',
		redirect_uris: [],
		confidential: true,
	},
];

function splitUris(text) {
	return String(text || '')
		.split(/[\n,]/)
		.map((s) => s.trim())
		.filter(Boolean);
}

export function atlasConnectorAdmin(http, opts = {}) {
	const confirm = opts.confirm || ((msg) => (typeof window !== 'undefined' ? window.confirm(msg) : true));

	const self = {
		loading: ref(false),
		issuer: ref(''),
		tenants: ref([]),
		searchText: ref(''),
		onlyWithConnectors: ref(false),

		createDialog: reactive({
			show: false,
			busy: false,
			tenant: null,
			preset: 'copilot',
			client_name: 'Copilot Studio',
			redirect_uris_text: HOST_PRESETS[0].redirect_uris.join('\n'),
			confidential: true,
			scope: DEFAULT_SCOPE,
			error: '',
			// Populated once registration succeeds. The secret is shown ONCE.
			result: null,
		}),

		editDialog: reactive({
			show: false,
			busy: false,
			tenant: null,
			client: null,
			redirect_uris_text: '',
			error: '',
		}),

		tokenDialog: reactive({
			show: false,
			busy: false,
			tenant: null,
			label: '',
			usesRemaining: 1,
			expiresInHours: 24,
			error: '',
			result: null,
		}),
	};

	// ── Derived ──
	self.rows = computed(() => {
		const q = (self.searchText.value || '').trim().toLowerCase();
		return self.tenants.value
			.map((t) => {
				const active = (t.connectors || []).filter((c) => !c.revoked);
				const liveTokens = (t.tokens || []).filter(
					(k) => !k.revoked && k.uses_remaining > 0 && new Date(k.expires) > new Date(),
				);
				const lastUsed = active
					.map((c) => c.last_used)
					.filter(Boolean)
					.sort()
					.pop();
				return {
					...t,
					active_connectors: active.length,
					active_tokens: liveTokens.length,
					last_used: lastUsed || null,
					has_records: (t.connectors || []).length > 0 || (t.tokens || []).length > 0,
				};
			})
			.filter((t) => !q || String(t.client_name).toLowerCase().includes(q) || String(t.client_id) === q)
			.filter((t) => !self.onlyWithConnectors.value || t.has_records);
	});

	self.totalConnectors = computed(() =>
		self.tenants.value.reduce((n, t) => n + (t.connectors || []).filter((c) => !c.revoked).length, 0),
	);

	self.parsedRedirectUris = computed(() => splitUris(self.createDialog.redirect_uris_text));
	self.parsedEditRedirectUris = computed(() => splitUris(self.editDialog.redirect_uris_text));

	self.canCreate = computed(
		() => !!self.createDialog.tenant && self.parsedRedirectUris.value.length > 0 && !self.createDialog.busy,
	);

	/** Rows for the "copy these into the host" panel after a successful registration. */
	self.resultRows = computed(() => {
		const r = self.createDialog.result;
		if (!r) return [];
		return [
			{ k: 'Client ID', v: r.client_id },
			{ k: 'Client secret', v: r.client_secret, secret: true },
			{ k: 'Authorization URL', v: r.authorization_url },
			{ k: 'Token URL', v: r.token_url },
			{ k: 'Refresh URL', v: r.token_url },
			{ k: 'Scopes', v: r.scope },
			{ k: 'Redirect URIs', v: (r.redirect_uris || []).join('\n') },
		].filter((row) => !!row.v);
	});

	// ── Formatting helpers ──
	self.fmtDate = (v) => {
		if (!v) return '—';
		const d = new Date(v);
		return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
	};
	self.authMethodLabel = (m) =>
		m === 'none' ? 'Public (PKCE)' : m === 'client_secret_post' ? 'Confidential (secret)' : m;
	self.shortId = (id) => (id && id.length > 14 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id);
	self.tokenState = (k) => {
		if (k.revoked) return { text: 'Revoked', color: 'grey', live: false };
		if (new Date(k.expires) <= new Date()) return { text: 'Expired', color: 'grey', live: false };
		if (k.uses_remaining <= 0) return { text: 'Used', color: 'blue-grey', live: false };
		return {
			text: `Live (${k.uses_remaining} use${k.uses_remaining === 1 ? '' : 's'})`,
			color: 'green',
			live: true,
		};
	};

	self.copy = async (text, notify) => {
		try {
			await navigator.clipboard.writeText(String(text));
			notify?.('Copied to clipboard', 'success');
		} catch (e) {
			notify?.('Copy failed — select and copy manually', 'error');
		}
	};

	// ── Data ──
	self.fetchData = async (notify) => {
		self.loading.value = true;
		try {
			const resp = await http.get('api/oauth/admin/internal/overview');
			if (resp.data?.Status !== 'OK') throw new Error(resp.data?.error || 'Bad response');
			self.tenants.value = resp.data.Data || [];
			self.issuer.value = resp.data.issuer || '';
		} catch (error) {
			console.error('Error loading Atlas connector overview', error);
			notify?.('Error loading connectors', 'error');
		} finally {
			self.loading.value = false;
		}
	};

	/** Re-resolve a tenant row by id after a refresh (rows are rebuilt). */
	self.findTenant = (tenantId) => self.tenants.value.find((t) => Number(t.client_id) === Number(tenantId)) || null;

	// ── Create connector (mint + register) ──
	self.applyPreset = (key) => {
		const p = HOST_PRESETS.find((x) => x.key === key) || HOST_PRESETS[HOST_PRESETS.length - 1];
		self.createDialog.preset = p.key;
		self.createDialog.client_name = p.client_name;
		self.createDialog.redirect_uris_text = p.redirect_uris.join('\n');
		self.createDialog.confidential = p.confidential;
	};

	self.openCreate = (tenant) => {
		const d = self.createDialog;
		d.tenant = tenant;
		d.busy = false;
		d.error = '';
		d.result = null;
		d.scope = DEFAULT_SCOPE;
		self.applyPreset('copilot');
		d.show = true;
	};

	self.closeCreate = () => {
		self.createDialog.show = false;
		self.createDialog.result = null;
	};

	self.submitCreate = async (notify) => {
		const d = self.createDialog;
		if (!self.canCreate.value) return;
		d.busy = true;
		d.error = '';
		try {
			const resp = await http.post(`api/oauth/admin/internal/tenants/${d.tenant.client_id}/connectors`, {
				client_name: d.client_name,
				redirect_uris: self.parsedRedirectUris.value,
				confidential: !!d.confidential,
				scope: d.scope,
			});
			if (resp.data?.Status !== 'OK') throw new Error(resp.data?.error || 'Registration failed');
			d.result = resp.data;
			notify?.('Connector registered', 'success');
			await self.fetchData(notify);
		} catch (error) {
			console.error('Error creating connector', error);
			d.error = error?.response?.data?.error || error?.message || 'Registration failed';
		} finally {
			d.busy = false;
		}
	};

	// ── Edit redirect URIs on an existing connector (id / secret unchanged) ──
	self.openEditRedirects = (tenant, client) => {
		const d = self.editDialog;
		d.tenant = tenant;
		d.client = client;
		d.redirect_uris_text = (client.redirect_uris || []).join('\n');
		d.error = '';
		d.busy = false;
		d.show = true;
	};

	self.submitEditRedirects = async (notify) => {
		const d = self.editDialog;
		if (!d.client || !self.parsedEditRedirectUris.value.length) return;
		d.busy = true;
		d.error = '';
		try {
			const resp = await http.post(
				`api/oauth/admin/internal/tenants/${d.tenant.client_id}/clients/${encodeURIComponent(d.client.client_id)}/redirect-uris`,
				{ redirect_uris: self.parsedEditRedirectUris.value },
			);
			if (resp.data?.Status !== 'OK') throw new Error(resp.data?.error || 'Update failed');
			notify?.('Redirect URIs updated', 'success');
			d.show = false;
			await self.fetchData(notify);
		} catch (error) {
			console.error('Error updating redirect URIs', error);
			d.error = error?.response?.data?.error || error?.message || 'Update failed';
		} finally {
			d.busy = false;
		}
	};

	// ── Mint a raw connector token only ──
	self.openMintToken = (tenant) => {
		const d = self.tokenDialog;
		d.tenant = tenant;
		d.busy = false;
		d.error = '';
		d.result = null;
		d.label = '';
		d.usesRemaining = 1;
		d.expiresInHours = 24;
		d.show = true;
	};

	self.submitMintToken = async (notify) => {
		const d = self.tokenDialog;
		d.busy = true;
		d.error = '';
		try {
			const resp = await http.post(`api/oauth/admin/internal/tenants/${d.tenant.client_id}/connector-tokens`, {
				label: d.label || null,
				usesRemaining: Number(d.usesRemaining) || 1,
				expiresInHours: Number(d.expiresInHours) || 24,
			});
			if (resp.data?.Status !== 'OK') throw new Error(resp.data?.error || 'Mint failed');
			d.result = resp.data;
			notify?.('Connector token minted', 'success');
			await self.fetchData(notify);
		} catch (error) {
			console.error('Error minting connector token', error);
			d.error = error?.response?.data?.error || error?.message || 'Mint failed';
		} finally {
			d.busy = false;
		}
	};

	// ── Revoke ──
	self.revokeClient = async (tenant, client, notify) => {
		if (
			!confirm(
				`Revoke connector "${client.client_name}" (${client.client_id})?\nExisting tokens issued to it stop working.`,
			)
		)
			return;
		try {
			const resp = await http.post(
				`api/oauth/admin/internal/tenants/${tenant.client_id}/clients/${encodeURIComponent(client.client_id)}/revoke`,
			);
			if (resp.data?.Status !== 'OK') throw new Error('Revoke failed');
			notify?.('Connector revoked', 'success');
			await self.fetchData(notify);
		} catch (error) {
			console.error('Error revoking connector', error);
			notify?.('Error revoking connector', 'error');
		}
	};

	self.revokeToken = async (tenant, token, notify) => {
		if (!confirm(`Revoke connector token "${token.label || token.token_hash.slice(0, 12)}"?`)) return;
		try {
			const resp = await http.post(
				`api/oauth/admin/internal/tenants/${tenant.client_id}/connector-tokens/${token.token_hash}/revoke`,
			);
			if (resp.data?.Status !== 'OK') throw new Error('Revoke failed');
			notify?.('Token revoked', 'success');
			await self.fetchData(notify);
		} catch (error) {
			console.error('Error revoking token', error);
			notify?.('Error revoking token', 'error');
		}
	};

	return self;
}
